import { buffCountdownFresh, observationFresh, type BuffObservation } from './types'
import type { BuffNumberRead } from './timer'

/** One scheduler for all targets. Burst deadlines never slide on repeated evidence. */
export class BuffSampling {
  private bursts = new Map<string, { until: number; retryAt: number; once: boolean }>()
  private observations: BuffObservation[] = []
  private reads: Record<string, BuffNumberRead> = {}
  private lastNumbers = new Map<string, number>()
  constructor(public baseIntervalMs = 500) {}
  reset(): void { this.bursts.clear(); this.lastNumbers.clear(); this.observations = []; this.reads = {} }
  configure(interval: number): void { this.baseIntervalMs = interval }
  observe(observations: BuffObservation[], reads: Record<string, BuffNumberRead>): void {
    this.observations = observations; this.reads = reads
  }
  interval(now: number, thresholds: ReadonlyMap<string, number>): number {
    if (this.baseIntervalMs <= 500) return this.baseIntervalMs
    const wanted = new Set<string>()
    const request = (o: BuffObservation, reason: string, duration: number, once = false) => {
      const key = `${o.id}:${o.cycle}:${reason}`; wanted.add(key)
      const old = this.bursts.get(key)
      if (!old || (!old.once && now >= old.retryAt)) this.bursts.set(key, { until: now + duration, retryAt: now + duration + 8000, once })
    }
    for (const o of this.observations) {
      if (!thresholds.has(o.id) || !observationFresh(o, now) || o.presence === 'missing') continue
      const read = this.reads[o.id]
      const credible = read && read.usable !== false && read.score >= .82 && read.candidate >= 0 && read.candidate <= 59
      const numberKey = `${o.id}:${o.cycle}:number`
      if (credible) {
        const previous = this.lastNumbers.get(o.id)
        // A newly changed counter starts a new evidence sequence, even if a
        // preceding unchanged coarse counter used its ordinary burst already.
        if (previous !== undefined && read.candidate > previous + 2) this.bursts.delete(numberKey)
        this.lastNumbers.set(o.id, read.candidate)
      }
      if (o.timeReason === 'number-conflict' || o.timeReason === 'number-conflict-pending') request(o, 'conflict', 6000)
      else if (o.seconds === null && (credible || now <= (this.bursts.get(numberKey)?.until ?? -Infinity)) && (o.reason === 'confirming' || o.presence === 'present')) request(o, 'number', 6000)
      // Locate digits while the icon is already confirmed, and inspect weak
      // reads before the next coarse sample would outlive the numeric lease.
      // This requests evidence; it never extends a timer or accepts a conflict.
      const locating = read?.reason === 'number-search-pending'
      const established = o.numberEvidenceAt !== undefined && o.numberValidUntil !== undefined
      const endangered = established && (read?.usable === false || now + this.baseIntervalMs >= o.numberValidUntil!)
      const evidenceBurstActive = now <= (this.bursts.get(`${o.id}:${o.cycle}:evidence`)?.until ?? -Infinity)
      if (o.presence === 'present' && (locating || endangered || evidenceBurstActive)) request(o, 'evidence', 6000)
      const gap = o.reason === 'confirming-missing' || o.reason === 'search-incomplete' || o.reason === 'tracking-gap' || o.reason === 'local-reacquire'
      if (gap && o.cycle > 0) request(o, 'missing', 6000)
      else if (o.presence === 'present' && !o.held) this.bursts.delete(`${o.id}:${o.cycle}:missing`)
      if (o.timeReason === 'renewal-confirming') request(o, 'renewal', 8000)
      if (o.reason === 'confirming') request(o, 'confirming', 6000)
      // The deadline is only a reason to inspect, never proof of disappearance.
      // Games may keep the icon visible briefly after its last numeric second.
      if (o.timeReason === 'endpoint-passed' && o.cycle > 0) request(o, 'end-check', 6000, true)
      if (o.upperSeconds !== null && buffCountdownFresh(o, now)) {
        const left = o.upperSeconds - Math.max(0, now - o.capturedAt) / 1000
        if (left <= thresholds.get(o.id)! + 2) request(o, 'threshold', 15000, true)
        if (left <= 12) request(o, 'ending', 15000, true)
      }
    }
    for (const [key, burst] of this.bursts) {
      const prefix = this.observations.some(o => key.startsWith(`${o.id}:${o.cycle}:`) && thresholds.has(o.id))
      if (!prefix) this.bursts.delete(key)
      else if (!wanted.has(key)) burst.until = Math.min(burst.until, now)
    }
    return [...this.bursts.entries()].some(([key, b]) => wanted.has(key) && b.until >= now) ? 500 : this.baseIntervalMs
  }
}

/** Wall-clock changes cannot add or remove seconds from an active monitoring run. */
export class BuffClock {
  private epoch = Date.now()
  private origin = performance.now()
  now = (): number => this.epoch + performance.now() - this.origin
}
